import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { DatePicker } from "antd";
import { LoadingIcon, TopDropdown } from "../../../components";
import { getDatesForSubplotData, getGreenhouses } from "../../../utils/api";
import { UserContext } from "../../../context/UserContext";
import { useLocalStorage } from "../../../hooks";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../consts";

dayjs.extend(isoWeek);

const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  setCurrentMetric,
  setMetricType,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  plotData,
  setPlotData,
  currentSubplot,
  setCurrentSubplot,
  subplotData,
  setSubplotData,
  currentGreenhouse,
  setCurrentGreenhouse,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  currentDate,
  setCurrentDate,
  setMetricData,
  greenhouseData,
  setGreenhouseData,
  setDropdownLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  dateData,
  setDateData,
  disabledDate,
}) => {
  const [defaultDropdown, setDefaultDropdown] = useLocalStorage(
    "dropdowns",
    null,
  );
  const { userData } = useContext(UserContext);

  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [plotLabel, setPlotLabel] = useState("Plot");
  const [subplotLabel, setSubplotLabel] = useState("Subplot");

  const [greenhouseLoading, setGreenhouseLoading] = useState(false);
  const [subplotLoading, setSubplotLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);

  const getDefaultSelectableDate = (dateData) => {
    if (!Array.isArray(dateData) || !dateData.length) return null;

    const today = dayjs().startOf("day");
    return today.add(1, "day").format(BACKEND_DATE_FORMAT);
  };

  // -----------------------------------
  // Fetch greenhouses from API
  // -----------------------------------
  const fetchGreenhouses = async (orgId, siteId, cropId) => {
    setGreenhouseLoading(true);
    const res = await getGreenhouses(orgId, siteId, cropId);
    const greenhouseList = Array.isArray(res?.data?.data)
      ? res.data.data
      : [];
    const data = greenhouseList.map((greenhouse) => ({
      name: greenhouse.name,
      id: greenhouse.id,
      plots: (greenhouse.plots || []).map((plot) => ({
        ...plot,
        greenhouse_id: greenhouse.id,
        greenhouse_name: greenhouse.name,
      })),
      metadata: greenhouse.metadata || {},
    }));
    setGreenhouseData(data);
    setGreenhouseLoading(false);
  };

  // -----------------------------------
  // DatePicker callback
  // -----------------------------------
  const handleDateChange = (date) => {
    if (date) {
      setCurrentDate(date.format(BACKEND_DATE_FORMAT));
    } else {
      setCurrentDate(null);
    }
  };

  // -----------------------------------
  // Whenever site/org/crop changes => fetch greenhouses
  // -----------------------------------
  useEffect(() => {
    if (currentSite && currentOrg) {
      fetchGreenhouses(currentOrg, currentSite.id, currentCrop?.id);
    }
  }, [currentSite, currentOrg, currentCrop]);

  // -----------------------------------
  // If siteMetadata has greenhouse_name etc., override labels
  // -----------------------------------
  useEffect(() => {
    if (siteMetadata && Object.keys(siteMetadata).length > 0) {
      if (siteMetadata?.greenhouse_name) {
        setGreenHouseLabel(siteMetadata.greenhouse_name);
      }
      if (siteMetadata?.plot_name) {
        setPlotLabel(siteMetadata.plot_name);
      }
      if (siteMetadata?.subplot_name) {
        setSubplotLabel(siteMetadata.subplot_name);
      }
    }
  }, [siteMetadata]);

  // -----------------------------------
  // Once greenhouseData is loaded => pick default greenhouse from:
  //   1) URL param
  //   2) localStorage (store ID)
  //   3) fallback => greenhouseData[0]
  // Then set plotData from that greenhouse
  // -----------------------------------
  useEffect(() => {
    if (greenhouseData?.length) {
      const params = new URLSearchParams(searchParams);
      const ghIdFromUrl = params.get("greenhouse");

      let chosenGH = null;

      if (ghIdFromUrl) {
        const found = greenhouseData.find((gh) => gh.id === ghIdFromUrl);
        chosenGH = found || greenhouseData[0];
      } else if (!settingDefaults.greenhouse) {
        const storedGHId = defaultDropdown?.[currentOrg]?.greenhouse;
        if (storedGHId) {
          const ghObj = greenhouseData.find((gh) => gh.id === storedGHId);
          chosenGH = ghObj || greenhouseData[0];
        } else {
          chosenGH = greenhouseData[0];
        }
        setSettingDefaults((prev) => ({ ...prev, greenhouse: true }));
      } else {
        chosenGH = greenhouseData[0];
      }

      setCurrentGreenhouse(chosenGH);
      setPlotData(chosenGH.plots || []);
    } else {
      setCurrentGreenhouse(null);
      setPlotData(null);
    }
  }, [greenhouseData]);

  // useEffect(() => {
  //   if (plotData && plotData.length) {
  //     let params = new URLSearchParams(searchParams);
  //     let plotIdFromUrlParams = params.get("plot");
  //     if (plotIdFromUrlParams) {
  //       const plotFromUrl = plotData.find(
  //         (item) => item.id === plotIdFromUrlParams,
  //       );
  //       if (plotFromUrl) {
  //         setCurrentPlot(plotFromUrl);
  //       } else {
  //         setCurrentPlot(plotData[0]);
  //       }
  //     } else if (!settingDefaults.plot) {
  //       let plotVal = plotData[0];
  //       if (defaultDropdown && defaultDropdown[currentOrg]?.plot) {
  //         let defaultPlotValue = plotData.find(
  //           (item) => item.id === defaultDropdown[currentOrg]?.plot.id,
  //         );
  //         plotVal = defaultPlotValue ? defaultPlotValue : plotData[0];
  //       }
  //       setCurrentPlot(plotVal);
  //       setSettingDefaults({ ...settingDefaults, plot: true });
  //     } else {
  //       setCurrentPlot(plotData[0]);
  //     }
  //   } else {
  //     setCurrentPlot(null);
  //   }
  // }, [plotData]);

  useEffect(() => {
    if (currentSite && currentOrg && currentCrop && currentGreenhouse) {
      setPlotData(currentGreenhouse.plots);
    }
  }, [currentGreenhouse]);

  // -----------------------------------
  // On greenhouse change => fetch date data
  // -----------------------------------
  useEffect(() => {
    if (currentSite && currentOrg && currentCrop && currentGreenhouse) {
      const fetchDates = async () => {
        setDateLoading(true);
        try {
          const response = await getDatesForSubplotData(
            currentOrg,
            currentSite.id,
            currentCrop.id,
            [currentGreenhouse.id],
            null,
            null,
          );
          if (response.data.success) {
            const dateSet = new Set();
            response.data.data.forEach((item) => {
              item.dates.forEach((d) => dateSet.add(d));
            });
            setDateData([...dateSet].sort((a, b) => dayjs(b).diff(dayjs(a))));
          } else {
            setDateData([]);
          }
        } catch (error) {
          console.error("Error fetching dates:", error);
          setDateData([]);
        } finally {
          setDateLoading(false);
        }
      };
      fetchDates();
    }
  }, [currentGreenhouse]);

  // useEffect(() => {
  //   const fetchSubplots = async () => {
  //     if (currentOrg && currentSite?.id && currentCrop?.id && currentPlot?.id) {
  //       setSubplotLoading(true);
  //       try {
  //         const response = await getSubPlots(
  //           currentOrg,
  //           currentSite.id,
  //           currentCrop.id,
  //           [currentPlot.id],
  //         );
  //         if (response.data.success) {
  //           setSubplotData(response.data.data);
  //         } else {
  //           setSubplotData([]);
  //         }
  //       } catch (error) {
  //         setSubplotData([]);
  //       } finally {
  //         setSubplotLoading(false);
  //       }
  //     }
  //   };
  //   fetchSubplots();
  // }, [currentPlot]);

  // useEffect(() => {
  //   if (subplotData && subplotData.length) {
  //     let params = new URLSearchParams(searchParams);
  //     let subplotIdFromUrlParams = params.get("subplot");
  //     if (subplotIdFromUrlParams) {
  //       const subplotFromUrl = subplotData.find(
  //         (item) => item.id === subplotIdFromUrlParams,
  //       );
  //       if (subplotFromUrl) {
  //         setCurrentSubplot(subplotFromUrl);
  //       } else {
  //         setCurrentSubplot(subplotData[0]);
  //       }
  //     } else if (!settingDefaults.subplot) {
  //       let subplotVal = subplotData[0];
  //       if (defaultDropdown && defaultDropdown[currentOrg]?.subplot) {
  //         let defaultSubplotValue = subplotData.find(
  //           (item) => item.id === defaultDropdown[currentOrg]?.subplot.id,
  //         );
  //         subplotVal = defaultSubplotValue
  //           ? defaultSubplotValue
  //           : subplotData[0];
  //       }
  //       setCurrentSubplot(subplotVal);
  //       setSettingDefaults({ ...settingDefaults, subplot: true });
  //     } else {
  //       setCurrentSubplot(subplotData[0]);
  //     }
  //   } else {
  //     setCurrentSubplot(null);
  //   }
  // }, [subplotData]);

  // useEffect(() => {
  //   const fetchDates = async () => {
  //     if (
  //       currentOrg &&
  //       currentSite?.id &&
  //       currentCrop?.id &&
  //       currentGreenhouse?.id &&
  //       currentPlot?.id &&
  //       currentSubplot?.id
  //     ) {
  //       setDateLoading(true);
  //       try {
  //         const response = await getDatesForSubplotData(
  //           currentOrg,
  //           currentSite.id,
  //           currentCrop.id,
  //           [currentGreenhouse.id],
  //           [currentPlot.id],
  //           [currentSubplot.id],
  //         );
  //         if (response.data.success) {
  //           setDateData(response.data.data?.[0]?.dates || []);
  //         } else {
  //           setDateData([]);
  //         }
  //       } catch (error) {
  //         console.error("Error fetching dates:", error);
  //         setDateData([]);
  //       } finally {
  //         setDateLoading(false);
  //       }
  //     }
  //   };
  //   fetchDates();
  // }, [currentSubplot]);

  useEffect(() => {
    if (!dateData.length) return;

    const params = new URLSearchParams(searchParams);
    const urlDate = params.get("date");
    const isValidUrl = urlDate && dateData.includes(urlDate);

    const chosenDate = isValidUrl
      ? urlDate
      : getDefaultSelectableDate(dateData);

    if (!chosenDate) return;

    setCurrentDate(chosenDate);

    // keep the address bar in sync only if we changed it
    if (!isValidUrl || urlDate !== chosenDate) {
      params.set("date", chosenDate);
      setSearchParams(params);
    }
  }, [dateData]);

  // -----------------------------------
  // Whenever greenhouse changes => store ID in URL param + localStorage
  // -----------------------------------
  useEffect(() => {
    if (currentGreenhouse) {
      const params = new URLSearchParams(searchParams);
      params.set("greenhouse", currentGreenhouse.id);
      setSearchParams(params);

      const orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          greenhouse: currentGreenhouse.id,
        },
      });
    }
  }, [currentGreenhouse]);

  // useEffect(() => {
  //   if (currentPlot) {
  //     let existingParams = new URLSearchParams(searchParams);
  //     existingParams.set("plot", currentPlot.id);
  //     setSearchParams(existingParams);
  //     let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
  //     setDefaultDropdown({
  //       ...defaultDropdown,
  //       [currentOrg]: {
  //         ...orgObj,
  //         plot: currentPlot,
  //       },
  //     });
  //   }
  // }, [currentPlot]);

  // useEffect(() => {
  //   if (currentSubplot) {
  //     let existingParams = new URLSearchParams(searchParams);
  //     existingParams.set("subplot", currentSubplot.id);
  //     setSearchParams(existingParams);
  //     let orgObj = defaultDropdown ? defaultDropdown[currentOrg] : {};
  //     setDefaultDropdown({
  //       ...defaultDropdown,
  //       [currentOrg]: {
  //         ...orgObj,
  //         subplot: currentSubplot,
  //       },
  //     });
  //   }
  // }, [currentSubplot]);

  useEffect(() => {
    if (currentDate) {
      const params = new URLSearchParams(searchParams);
      params.set("date", currentDate);
      setSearchParams(params);

      let orgObj = defaultDropdown?.[currentOrg] || {};
      setDefaultDropdown({
        ...defaultDropdown,
        [currentOrg]: {
          ...orgObj,
          date: currentDate,
        },
      });
    }
  }, [currentDate]);

  // -----------------------------------
  // Overall loading state
  // -----------------------------------
  useEffect(() => {
    setDropdownLoading(
      orgLoading ||
        siteLoading ||
        cropLoading ||
        greenhouseLoading ||
        subplotLoading ||
        dateLoading,
    );
  }, [
    orgLoading,
    cropLoading,
    siteLoading,
    greenhouseLoading,
    subplotLoading,
    dateLoading,
  ]);

  return (
    <>
      {orgLoading || siteLoading || greenhouseLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 sm:mb-4 dropdowns !font-MuseoSans">
          {userData?.is_superuser && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[200px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[200px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[200px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />

          <TopDropdown
            label={`Select ${greenHouseLabel}:`}
            val={currentGreenhouse?.id ?? undefined}
            placeholder={`Select ${greenHouseLabel}`}
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(ghId) => {
              const ghObj = greenhouseData.find((gh) => gh.id === ghId);
              setCurrentGreenhouse(ghObj || null);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={greenhouseData?.map((greenhouse) => ({
              value: greenhouse.id,
              label: greenhouse.name,
            }))}
          />

          {/* Variety dropdown as multiple select with maxTagCount */}
          {/*<TopDropdown*/}
          {/*  label={`Select ${plotLabel}:`}*/}
          {/*  val={*/}
          {/*    currentPlot !== null ? JSON.stringify(currentPlot) : currentPlot*/}
          {/*  }*/}
          {/*  placeholder={`Select ${plotLabel}`}*/}
          {/*  classString="flex-1 min-w-[150px] sm:mb-2"*/}
          {/*  changeCallback={(val) => setCurrentPlot(JSON.parse(val))}*/}
          {/*  showSearch*/}
          {/*  optionFilterProp="children"*/}
          {/*  filterOption={(input, option) =>*/}
          {/*    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())*/}
          {/*  }*/}
          {/*  filterSort={(optionA, optionB) =>*/}
          {/*    (optionA?.label ?? "")*/}
          {/*      .toLowerCase()*/}
          {/*      .localeCompare((optionB?.label ?? "").toLowerCase())*/}
          {/*  }*/}
          {/*  data={plotData?.map((plot) => ({*/}
          {/*    label: plot.name,*/}
          {/*    value: JSON.stringify(plot),*/}
          {/*  }))}*/}
          {/*/>*/}

          {/*<TopDropdown*/}
          {/*  label={`Select ${subplotLabel}:`}*/}
          {/*  val={*/}
          {/*    currentSubplot !== null*/}
          {/*      ? JSON.stringify(currentSubplot)*/}
          {/*      : currentSubplot*/}
          {/*  }*/}
          {/*  placeholder={`Select ${subplotLabel}`}*/}
          {/*  classString="flex-1 min-w-[150px] sm:mb-2"*/}
          {/*  changeCallback={(val) => setCurrentSubplot(JSON.parse(val))}*/}
          {/*  showSearch*/}
          {/*  optionFilterProp="children"*/}
          {/*  filterOption={(input, option) =>*/}
          {/*    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())*/}
          {/*  }*/}
          {/*  filterSort={(optionA, optionB) =>*/}
          {/*    (optionA?.label ?? "")*/}
          {/*      .toLowerCase()*/}
          {/*      .localeCompare((optionB?.label ?? "").toLowerCase())*/}
          {/*  }*/}
          {/*  data={subplotData?.map((subplot) => ({*/}
          {/*    label: subplot.name,*/}
          {/*    value: JSON.stringify(subplot),*/}
          {/*  }))}*/}
          {/*/>*/}

          <div className="flex flex-col">
            <div className="text-sm text-darkerGray max-w-full mb-1">
              Select Date:
            </div>
            <DatePicker
              value={currentDate ? dayjs(currentDate) : null}
              className="flex-1 min-w-[200px] sm:mb-2"
              disabledDate={disabledDate}
              onChange={handleDateChange}
              format={UI_DATE_FORMAT}
              placeholder="Select Date"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Dropdowns;
